<template>
  <div class="child-page bg-blue-50">
    <b-container fluid="xl child-card-header d-none d-lg-block">
      <b-row>
        <b-col cols="auto" class="child-card-logo mx-4 px-4">
          <router-link :to="{ name: 'Home' }" custom v-slot="{ href, navigate }">
            <a :href="href" @click="navigate" class="bg-white rounded-circle router-link d-block">
              <span class="d-none">{{ $t('home.logo') }}</span>
              <b-img
                class="m-4 pt-3 px-2"
                fluid
                height="78px"
                width="88px"
                :src="logoImage"
                alt="Logo"
              ></b-img>
            </a>
          </router-link>
        </b-col>
      </b-row>
    </b-container>
    <b-container fluid="xl">
      <b-row align-v="stretch" align-h="center" class="mx-lg-4 px-lg-4 pb-4">
        <b-col lg="10" class="mt-3 mt-lg-1 mb-2">
          <BackButton :to="returnRoute"></BackButton>
        </b-col>
        <b-col v-if="loading === false" lg="6">
          <WChildCard v-bind="childCard" class="child-page__child-card"></WChildCard>
        </b-col>
        <b-col lg="6" v-else>
          <b-skeleton animation="none" height="500px" width="100%" class="mb-0"></b-skeleton>
        </b-col>
        <b-col v-if="loading === false" lg="4" class="mt-4 mt-lg-0">
          <component
            :is="mapFundraisingComponents[fundraisingComponent]"
            :childData="getChild"
            :loader="loading"
            :is-fundraising="isOpenFundraising || isClosedFundraising"
            :showHelpButton="showHelpButton"
            :isPaymentAllowedForChild="isPaymentAllowedForChild"
          ></component>
        </b-col>
        <b-col lg="4" v-else>
          <b-skeleton animation="none" height="500px" width="100%" class="mb-0"></b-skeleton>
        </b-col>
        <b-col
          v-if="getChild?.beginning_of_our_story_child_choice_path"
          class="mt-4 d-block d-md-none"
        >
          <BegginingOfOurStory :content="getChild?.beginning_of_our_story_child_choice_path" />
        </b-col>
      </b-row>
    </b-container>
  </div>
  <div class="bg-mint py-5" v-if="dreamDrawingImagePublicUrls">
    <b-container fluid="xl">
      <b-row>
        <b-col cols="12">
          <WDreamDrawing
            :dreamDrawingImagePublicUrls="dreamDrawingImagePublicUrls"
            :dreamDrawingBreakpoints="dreamDrawingBreakpoints"
            :dreamDrawingLikeCard="dreamDrawingLikeCard"
          ></WDreamDrawing>
        </b-col>
      </b-row>
    </b-container>
  </div>
  <div
    :class="['bg-blue-50', { 'pt-4': getChild?.dream_drawing_image_public_urls }]"
    v-if="getChild?.beginning_of_our_story_child_choice_path"
  >
    <b-container fluid="xl" class="d-none d-md-block">
      <b-row align-h="center" class="mx-lg-4 px-lg-4 pb-4">
        <b-col lg="10">
          <BegginingOfOurStory :content="getChild?.beginning_of_our_story_child_choice_path" />
        </b-col>
      </b-row>
    </b-container>
  </div>
  <div class="bg-blue-50">
    <AcademyBenefits
      :childData="getChild"
      :showHelpButton="showHelpButton"
      :isPaymentAllowedForChild="isPaymentAllowedForChild"
      :female="getChild && getChild.gender.id === 1"
    ></AcademyBenefits>
    <NavigationBar
      :firstNameDative="getChild.first_name_dative"
      :showHelpButton="showHelpButton"
      v-if="!loading && isPaymentAllowedForChild"
    />
    <AppFooter class="footer-with-navigation-bar-margin"></AppFooter>
    <b-spinner v-if="loading"></b-spinner>
    <ScrollTopButton :bottom="100" />
    <ModalChildrenChosen v-if="!loading" :isChildChosen="!showHelpButton && isPrevRouteHomePage">
    </ModalChildrenChosen>
  </div>
</template>

<script setup lang="ts">
import useChildStore from '@/stores/child'
import useEditionStageStore from '@/stores/editionStage'
import { storeToRefs } from 'pinia'
import { computed, reactive } from 'vue'
import { useRouter, type RouteLocationNamedRaw } from 'vue-router'
// @ts-ignore
import { useStore } from 'vuex'

import AcademyBenefits from '@/components/childPage/AcademyBenefits.vue'
import ModalChildrenChosen from '@/components/childPage/ModalChildrenChosen.vue'
import NavigationBar from '@/components/childPage/NavigationBar.vue'
import BackButton from '@/components/global/BackButton.vue'
import AppFooter from '@/components/global/AppFooter.vue'
import ScrollTopButton from '@/components/widgets/ScrollTopButton.vue'
import BegginingOfOurStory from '@/components/childPage/BegginingOfOurStory.vue'
import redirectOnError from '@/router/helpers/redirectOnError'

import { FundraisingState } from '@/types'
import type { ImageBreakpoint } from '@wiosna-dev/wiosna-ui/types'
import type { components } from '@/types'

interface Props {
  childEditionId: string | number
  childEditionRetentionHash?: string
}
const props = defineProps<Props>()

const vuexStore = useStore()
const childStore = useChildStore()
const router = useRouter()
const editionStageStore = useEditionStageStore()
const { isRetention } = storeToRefs(editionStageStore)
const fetchChild = async () => {
  try {
    await vuexStore.dispatch('child/getChild', {
      isRetention: isRetention.value,
      childId: props.childEditionId,
      childEditionRetentionHash: props.childEditionRetentionHash
    })
    childStore.updateCurrentEdition(vuexStore.getters['child/getChild']?.edition?.current || false)
  } catch (error) {
    redirectOnError(router, error)
  }
}
await fetchChild()

const dreamDrawingBreakpoints: Record<string, ImageBreakpoint> = {
  drawingThumbnailSize: '290w',
  drawingMediumSize: '766w',
  drawingOriginalSize: '1000w'
}

const {
  isPaymentAllowedForChild,
  isPrevRouteHomePage,
  dreamDrawingLikeCard,
  dreamDrawingImagePublicUrls
} = storeToRefs(childStore)

const returnRoute = reactive<RouteLocationNamedRaw>({
  name: 'Home'
})

// @TODO: add type definition after we get them from the backend and switch to pinia store
const getChild = computed<components['schemas']['ChildEdition2']>(
  () => vuexStore.getters['child/getChild'] ?? {}
)

const showWaitingTime = computed<boolean>(
  () => emptyDonor.value || getChild.value?.open_fundraising === true
)

const childCard = computed(() => ({
  firstName: getChild.value?.first_name,
  birthYear: +getChild.value.birthyear,
  genderId: getChild.value.gender.id,
  avatarId: getChild.value.avatar_id,
  province: getChild.value.province,
  childDescription: getChild.value.guardian_consent_content_child_choice_path,
  daysWaiting: showWaitingTime.value ? getChild.value.days_waiting ?? undefined : 0,
  personalDifficulties: isOpenFundraising.value
    ? getChild.value.personal_difficulties?.map((difficulty) => difficulty.title)
    : undefined
}))

const emptyDonor = computed<boolean>(() => Boolean(getChild.value?.open_fundraising == null))
const loading = computed<boolean>(() => vuexStore.getters['child/getLoader'])
const isOpenFundraising = computed<boolean>(() => getChild.value?.open_fundraising === true)

const isInstallment = computed<boolean>(() => !!getChild.value?.active_installments)
const isClosedFundraising = computed<boolean>(() => {
  const isClosed = getChild.value?.open_fundraising === false
  const donations = getChild.value?.payments_child_choice_path?.length > 1

  return donations && isClosed && isInstallment.value === false
})
const fundraisingComponent = computed<FundraisingState>(() => {
  switch (true) {
    case emptyDonor.value === true:
      return FundraisingState.WaitingForDonor
    case isOpenFundraising.value:
      return FundraisingState.OpenFundraising
    case isClosedFundraising.value:
      return FundraisingState.ClosedFundraising
    default:
      return FundraisingState.ClosedWaitingForDonor
  }
})
const mapFundraisingComponents = {
  OpenFundraising: () => import('@/components/childPage/OpenFundraising.vue'),
  ClosedFundraising: () => import('@/components/childPage/ClosedFundraising.vue'),
  WaitingForDonor: () => import('@/components/childPage/WaitingForDonor.vue'),
  ClosedWaitingForDonor: () => import('@/components/childPage/ClosedWaitingForDonor.vue')
}
const showHelpButton = computed<boolean>(
  () =>
    isPaymentAllowedForChild.value &&
    (fundraisingComponent.value === FundraisingState.WaitingForDonor ||
      fundraisingComponent.value === FundraisingState.OpenFundraising)
)

const logoImage = new URL('/src/assets/images/logo.png', import.meta.url).href
</script>
<style lang="scss">
.child-page {
  &__child-card {
    transform: translateY(var(--child-card-header-avatar-offset));
  }
}
</style>
